import { HttpClient } from '@angular/common/http';
import { Component, effect, ElementRef, inject, OnInit, PLATFORM_ID, model, input, viewChild, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DefaultLangChangeEvent, TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { debounceTime, Subject } from 'rxjs';

import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { BuyETHWertComponent } from '../../modals/eth-buy-wert/buy-eth-wert.component';
import { WertComponent } from '../../modals/wert/wert.component';
import { AnalyticsService } from '../../services/analytics.service';
import { PresaleDataService } from '../../services/connection-services/data.service';
import { EvmPurchseService } from '../../services/evm-services/evm-purchase.service';
import { PopupService } from '../../services/popup.service';
import { SolPurchaseService } from '../../services/sol-services/sol-purchase.service';
import { CHAINS, convertNum, SwapStatus } from '../../shared/constants/app-enums';
import { SWAP_INPUT_CHANGE_DELAY_MS, SWAP_INPUT_DEBOUNCE_MS, SWAP_MAX_TRAILING_DEBOUNCE_MS } from '../../shared/constants/app-config.const';
import { getChainData, getDynamicAmountv2, getGasFees, getNativeAmount, getUSDTAmount } from '../../shared/constants/wallet-connect.const';
import { nativeList, tokenList } from '../../shared/constants/widget-enum';
import { IChainData } from '../../shared/interfaces/common.interface';
import { IBalnceDataEVM, ITokenData } from '../../shared/interfaces/wallet.interface';

@Component({
  selector: 'app-swap',
  templateUrl: './swap.component.html',
  styleUrls: ['./swap.component.scss'],
  standalone: false,
})
export class SwapComponentV2 implements OnInit {
  // model() inputs allow the parent to bind two-way [(isNative)] / [(isWert)] so that
  // internal state changes (toggleWert, setActiveTab, etc.) propagate back to the parent.
  readonly isNative = model<boolean>(false);
  readonly isWert = model<boolean>(false);
  readonly isModal = input<boolean>(false);
  readonly messageEvent = output<boolean>();
  readonly swapIn = viewChild<ElementRef<HTMLInputElement> | null>('swapIn');
  readonly dropDownBtn = viewChild<ElementRef<HTMLButtonElement> | null>('dropDownBtn');

  public stakeApy: number = 0;
  environtment = environment;
  baseToken?: number;
  swapToken?: number;
  swapTokenUpdate = new Subject<number | undefined>();
  baseTokenUpdate = new Subject<number | undefined>();
  currentLang!: string;
  SwapStatus = SwapStatus;
  lblockPrice = 0;
  isLowBalance = false;
  inProgress = false;

  isStake = false;
  minAmount = environment.minToken;

  modalRef?: BsModalRef;
  lowGas = false;
  wertMode: boolean = false;

  chainData!: IChainData;
  salePrice!: number;
  modeName: string = '';
  walletAddress: string = '';
  status: SwapStatus = SwapStatus.not_started;
  hash!: string;
  tokenName: string = 'USDC';
  gasFees!: number;
  cryptoName = environment.tokenName;
  allBalance: { native: number; usdt: number; usdc: number | null } = { native: 0, usdt: 0, usdc: 0 };
  selectedTab: string = 'ALL'; // Default selected tab
  activeToken!: ITokenData; // Default active token
  tokenList: Array<ITokenData> = [];
  tokenFilteredList: Array<ITokenData> = [];
  tokensData = tokenList;
  chainId: number = 0;
  balanceData: IBalnceDataEVM = {} as IBalnceDataEVM;
  activeChain: string = '';

  private readonly _platformId = inject(PLATFORM_ID);
  readonly dataService = inject(PresaleDataService);
  private readonly http = inject(HttpClient);
  private readonly modalService = inject(BsModalService);
  private readonly translate = inject(TranslateService);
  private readonly popupService = inject(PopupService);
  private readonly router = inject(Router);
  readonly purchaseService = inject(EvmPurchseService);
  private readonly solanaPurchaseService = inject(SolPurchaseService);
  private readonly analyticsService = inject(AnalyticsService);

  constructor() {
    this.currentLang = this.translate.currentLang;
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe((params: DefaultLangChangeEvent) => {
      this.currentLang = params.lang || 'en';
    });

    this.swapTokenUpdate.pipe(debounceTime(SWAP_INPUT_DEBOUNCE_MS), takeUntilDestroyed()).subscribe(() => {
      if (this.swapToken && this.swapToken >= 0) {
        this.calculateSwap();
      } else {
        this.baseToken = 0;
      }
    });

    this.baseTokenUpdate.pipe(debounceTime(SWAP_INPUT_DEBOUNCE_MS), takeUntilDestroyed()).subscribe(() => {
      if (this.baseToken && this.baseToken >= 0) {
        this.calculateSwapReverse();
      } else {
        this.swapToken = 0;
      }
    });

    effect(() => {
      const data = this.dataService.balanceData();
      if (data) {
        this.getBal();
        this.updateAllgetMethod();
        this.activeChain = this.dataService.activeChain;
        if (this.swapToken === 0 || this.baseToken === 0) this.max();
      }
    });

    effect(() => {
      const data = this.dataService.walletAddressSignal();
      if (data) {
        this.tokenList = [...nativeList.map((x) => x), ...this.tokensData.map((x) => x)];
        this.tokenList.sort(({ tokenUsdVal: a }, { tokenUsdVal: b }) => b - a);
        if (this.selectedTab === 'ALL') {
          if (this.dataService?.activeChain === CHAINS.EVM && this.dataService.walletAddress) {
            this.selectedTab = 'ETH';
          } else if (this.dataService?.activeChain === CHAINS.SOL && this.dataService.walletAddressSOL) {
            this.selectedTab = 'SOL';
          } else {
            this.selectedTab = 'ALL';
          }
        }
        this.setTab(this.selectedTab, undefined, false);
      }
    });

    effect(() => {
      const item = this.purchaseService.transactionUpdates();
      this.status = item.status;
      this.hash = item.hash as string;
      if (item.status === SwapStatus.complete) {
        clearTimeout(this.dataService.refreshId);
        this.dataService.getPresalesData(true);
      }
    });

    // React to model signal changes (from parent binding or internal set()).
    // Runs on first render AND whenever isWert/isNative changes — replaces ngOnChanges.
    effect(() => {
      const isWert = this.isWert();     // track
      this.isNative();                  // track
      // Delay lets any parent bindings settle before resetting swap state
      setTimeout(() => {
        this.swapToken = undefined;
        this.baseToken = undefined;
        this.isLowBalance = false;
        this.lowGas = false;
        this.checkLowBalance();
        if (!isWert && !this.isModal()) this.max();
      }, SWAP_INPUT_CHANGE_DELAY_MS);
    });
  }

  ngOnInit(): void {
    this.isLowBalance = false;
    if (isPlatformBrowser(this._platformId)) {
      if (this.isModal()) this.changeTab('ETH', 'ETH');
    }
  }

  async max() {
    let isNative = ['BNB', 'ETH', 'SOL'].includes(this.activeToken?.symbol);
    let maxVal = 0;
    const balanceData_ = { ...this.dataService.balanceDataEVM, ...this.dataService.balanceDataSOl };
    const chainId = this.chainData?.chainIdInt;
    const _maxTokensToBuy = this.activeChain === CHAINS.EVM ? balanceData_.maxTokensToBuyEVM : balanceData_.maxTokensToBuySOL;

    if (isNative) {
      maxVal = await getDynamicAmountv2(_maxTokensToBuy! * 1.01, this.activeToken?.symbol, this.activeToken?.category, [], chainId, balanceData_);
    } else if (this.activeToken?.symbol == 'USDT') {
      maxVal = await getDynamicAmountv2(_maxTokensToBuy!, 'USDT', this.activeToken.category, [], chainId, balanceData_);
    } else if (this.activeToken?.symbol == 'USDC') {
      maxVal = await getDynamicAmountv2(_maxTokensToBuy!, 'USDT', this.activeToken.category, [], chainId, balanceData_);
    } else {
      maxVal = await getDynamicAmountv2(_maxTokensToBuy!, this.activeToken?.symbol, this.activeToken?.category, [], chainId, balanceData_);
    }
    if (!this.activeToken) {
      this.swapToken = 0;
    } else {
      this.swapToken = +this.floorString((isNative ? (this.activeToken?.tokenBalance - this.gasFees > 0 ? this.activeToken?.tokenBalance - this.gasFees : 0) : this.activeToken?.tokenBalance)?.toString());
    }
    if (maxVal < this.swapToken) {
      this.swapToken = maxVal;
    }
    if (this.swapToken == 0) {
      this.swapToken = 0;
      this.baseToken = 0;
    } else {
      this.swapToken = Math.floor(this.swapToken * Math.pow(10, this.activeToken.desCount)) / Math.pow(10, this.activeToken.desCount);
      this.swapTokenUpdate.next(this.swapToken);
    }

    if (this.isWert()) this.swapToken = undefined;
    // Small trailing delay ensures the debounce chain fires after any synchronous state updates
    setTimeout(() => this.swapTokenUpdate.next(this.swapToken), SWAP_MAX_TRAILING_DEBOUNCE_MS);
  }

  getBal() {
    this.balanceData = this.dataService.balanceDataEVM;
    const _solBalanceData = this.dataService.balanceDataSOl;
    this.chainId = this.dataService.chainId;
    let ethPrice = this.balanceData.usdtBuyHelperETH / (this.balanceData.buyHelprETH * 1000000);
    let bnbPrice = this.balanceData.usdtBuyHelperETH / (this.balanceData.buyHelperBSC * 1000000);
    let solPrice = (_solBalanceData.solUsdt || 0) / ((_solBalanceData.oneSol || 0) * 1000000);

    this.tokenList = [...nativeList.map((x) => x), ...this.tokensData.map((x) => x)];
    // ETH TOKENS STARTS
    this.tokenList[0].tokenBalance = this.balanceData.nativeBalETH;
    this.tokenList[0].tokenUsdVal = this.balanceData.nativeBalETH * ethPrice;
    this.tokenList[1].tokenBalance = this.balanceData.usdtBalETH;
    this.tokenList[1].tokenUsdVal = this.balanceData.usdtBalETH;
    this.tokenList[2].tokenBalance = this.balanceData.usdcBalETH;
    this.tokenList[2].tokenUsdVal = this.balanceData.usdcBalETH;
    // ETH TOKENS ENDS

    // BSC TOKENS ENDS
    this.tokenList[3].tokenBalance = this.balanceData.nativeBalBSC;
    this.tokenList[3].tokenUsdVal = this.balanceData.nativeBalBSC * bnbPrice;
    this.tokenList[4].tokenUsdVal = this.balanceData.usdtBalBSC;
    this.tokenList[4].tokenBalance = this.balanceData.usdtBalBSC;
    this.tokenList[5].tokenUsdVal = this.balanceData.usdcBalBSC;
    this.tokenList[5].tokenBalance = this.balanceData.usdcBalBSC;
    // BSC TOKENS ENDS

    // Sol items
    this.tokenList[6].tokenBalance = _solBalanceData.nativeBalSol ?? 0;
    this.tokenList[7].tokenBalance = _solBalanceData.usdtBalSol ?? 0;
    this.tokenList[8].tokenBalance = _solBalanceData.usdcBalSol ?? 0;
    this.tokenList[6].tokenUsdVal = (_solBalanceData.nativeBalSol || 0) * solPrice;
    this.tokenList[7].tokenUsdVal = _solBalanceData.usdtBalSol ?? 0;
    this.tokenList[8].tokenUsdVal = _solBalanceData.usdcBalSol ?? 0;
    // for (let i = 6; i < this.tokenList.length; i++) {
    //   let tokenNativePrice = this.tokenList[i].category == 'ETH' ? ethPrice : bnbPrice;
    //   this.tokenList[i].tokenUsdVal = (tokenNativePrice * this.tokenList[i].tokenBalance) / this.tokenList[i].tokenPerNative!;
    // }
    this.tokenList.sort(({ tokenUsdVal: a }, { tokenUsdVal: b }) => b - a);
  }

  updateAllgetMethod(): void {
    const _balanceData = this.dataService.balanceDataEVM;
    this.chainData = getChainData(this.dataService.chainId) as IChainData;
    this.modeName = this.chainData.purchaseToken;
    this.walletAddress = this.activeChain === CHAINS.EVM ? this.dataService.walletAddress : this.dataService.walletAddressSOL;
    this.isNative.set(this.activeToken?.symbol !== 'USDT' && this.activeToken?.symbol !== 'USDC' && !this.isWert());
    this.tokenName = this.isWert() ? 'USD' : (this.activeToken?.symbol ?? 'USDC');
    // if (this.activeToken?.chainId == environment.eth.chainIdInt) {
    //     this.gasFees = this.dataService.gasFeesEth || getGasFees(this.activeToken?.chainId);
    // } else if (this.activeToken?.chainId == environment.bsc.chainIdInt) {
    //     this.gasFees = this.dataService.gasFeesBsc || getGasFees(this.activeToken?.chainId);
    // } else {
    //     this.gasFees = getGasFees(this.activeToken?.chainId);
    // }
    this.gasFees = this.dataService.gasFees;
    this.cryptoName = environment.tokenName;
    const currentStep = _balanceData?.currentStep;
    const currentStoreData = this.dataService.tokenomics[currentStep] || {};
    this.salePrice = currentStoreData?.tokenUSDT;
    this.stakeApy = this.dataService.stakeApy;
  }

  ngOnDestroy(): void {}

  startAgain(): void {
    this.getBal();
    this.updateAllgetMethod();
    this.max();
    this.purchaseService.resetTransactionStatus();
  }

  confirm(isBuyStake: boolean = false) {
    this.isStake = isBuyStake;
    const isWert = this.isWert();
    this.analyticsService.vwoData('buyButtonClicked', { walletAddress: this.walletAddress, isBuyAndStake: isBuyStake, isBuyWithCard: isWert });

    if (this.checkChain()) {
      if (isWert) {
        this.modalRef = this.modalService.show(WertComponent, {
          class: 'modal-xs modal-dialog-centered transak',
          backdrop: 'static',
          initialState: { walletAddress: this.walletAddress, baseToken: this.baseToken, swapToken: this.swapToken, isStake: this.isStake },
        });
      } else {
        if (this.swapToken !== 0 && (this.baseToken ?? 0) >= this.minAmount) this.createSwap(isBuyStake);
      }
    }
  }

  calculateSwap = async () => {
    this.checkLowBalance();
    const balanceData_ = { ...this.dataService.balanceDataEVM, ...this.dataService.balanceDataSOl };
    if (this.activeToken.symbol !== 'USDT' && this.activeToken.symbol !== 'USDC' && !this.isWert()) {
      this.baseToken = await getNativeAmount(+(this.swapToken ?? 0) * 0.99, this.activeToken.symbol, balanceData_);
    } else {
      this.baseToken = await getUSDTAmount(+(this.swapToken ?? 0) * 0.995, this.activeToken?.chainId, balanceData_); // hepler function for usdt and usdc are same so can use usdt for usdc as well
    }
    this.baseToken = this.baseToken && +this.baseToken.toFixed(0).replace(/0{0,0}$/, '');
  };

  calculateSwapReverse = async () => {
    const balanceData_ = { ...this.dataService.balanceDataEVM, ...this.dataService.balanceDataSOl };
    if (this.activeToken.symbol !== 'USDT' && this.activeToken.symbol !== 'USDC' && !this.isWert()) {
      this.swapToken = await getDynamicAmountv2(this.baseToken ?? 0, this.activeToken.symbol, this.activeToken.category, [], this.chainId, balanceData_);
      this.swapToken = +(this.swapToken * 1.01).toFixed(10).replace(/0{0,10}$/, '');
    } else {
      this.swapToken = await getDynamicAmountv2(this.baseToken ?? 0, 'USDT', this.activeToken.category, [], this.chainId, balanceData_); // hepler function for usdt and usdc are same so can use usdt for usdc as well
      this.swapToken = +(this.swapToken * 1.005).toFixed(10).replace(/0{0,10}$/, '');
    }
    // this.swapToken = Math.floor(this.swapToken * Math.pow(10, this.activeToken.desCount)) / Math.pow(10, this.activeToken.desCount)
    this.checkLowBalance();
  };

  async createSwap(isBuyStake: boolean = false) {
    if (this.inProgress) return;
    this.inProgress = true;
    const balanceData_ = { ...this.dataService.balanceDataEVM, ...this.dataService.balanceDataSOl };
    const _maxTokensToBuy = this.activeChain === CHAINS.EVM ? balanceData_.maxTokensToBuyEVM : balanceData_.maxTokensToBuySOL;

    if (this.baseToken && this.baseToken <= _maxTokensToBuy!) {
      try {
        this.activeChain === CHAINS.EVM ? await this.callEVMSwaps(isBuyStake) : await this.callSolanaSwaps(isBuyStake);
        this.inProgress = false;
      } catch (err: unknown) {
        // code 4001 = user rejected the transaction in their wallet
        const isUserRejection = typeof err === 'object' && err !== null && (err as { code?: number }).code === 4001;
        if (isUserRejection) {
          this.purchaseService.setTransactionStatus(SwapStatus.rejected);
        } else {
          this.purchaseService.setTransactionStatus(SwapStatus.failed);
        }
        this.inProgress = false;
      }
    } else {
      this.popupService.messagePopup({
        icon: 'warning',
        desc: this.translate.instant('widget.max_token_exceed', { maxTokensToBuy: _maxTokensToBuy }),
        title: this.translate.instant('widget.max_token_title'),
        confirmBtnTxt: this.translate.instant('misc.ok'),
      });
      this.inProgress = false;
    }
  }

  async callEVMSwaps(isBuyStake = false) {
    if (this.activeToken.symbol !== 'USDT' && this.activeToken.symbol !== 'USDC') {
      await this.purchaseService.swapNativeTokens(this.baseToken ?? 0, this.swapToken ?? 0, isBuyStake);
    } else {
      await this.purchaseService.swapCryptoForUSDT(this.baseToken ?? 0, this.swapToken ?? 0, false, isBuyStake, this.activeToken.symbol == 'USDC');
    }
  }

  async callSolanaSwaps(isBuyStake = false) {
    if (this.activeToken.symbol !== 'USDT' && this.activeToken.symbol !== 'USDC') {
      await this.solanaPurchaseService.swapNativeTokens(this.baseToken ?? 0, this.swapToken ?? 0, isBuyStake);
    } else {
      await this.solanaPurchaseService.buyWithStableToken(this.baseToken ?? 0, this.swapToken ?? 0, this.activeToken.symbol == 'USDC', false);
    }
  }

  viewUrl() {
    let blockUrl;
    if (this.activeChain === CHAINS.SOL) {
      blockUrl = environment.sol.blockExplorerURL;
    } else {
      blockUrl = this.chainData.blockExplorerURL;
    }
    const urlWindow = `${blockUrl}${this.hash}${this.activeChain === CHAINS.SOL && !environment.production ? '?cluster=devnet' : ''}`;
    if (typeof window !== 'undefined') {
      window.open(urlWindow, '_blank');
    }
  }

  checkLowBalance(): void {
    // let nativeBal = this.getNativeBal(this.activeToken.chainId);
    let nativeBal = this.dataService.gasFees;
    if (this.activeToken.symbol == 'ETH' || this.activeToken.symbol == 'BNB') {
      this.isLowBalance = +this.activeToken.tokenBalance - this.gasFees < +(this.swapToken ?? 0);
    } else {
      if (+this.activeToken.tokenBalance >= +(this.swapToken ?? 0) && +nativeBal >= this.gasFees) {
        this.isLowBalance = false;
        this.lowGas = false;
      } else {
        this.isLowBalance = true;
        if (nativeBal < this.gasFees) this.lowGas = true;
      }
    }
  }

  getNativeBal(chainId: number) {
    let balance = 0;
    const evmBalanceData = this.dataService.balanceDataEVM;
    const solBalanceData = this.dataService.balanceDataSOl;
    switch (chainId) {
      case environment.eth.chainIdInt:
        balance = evmBalanceData.nativeBalETH as number;
        break;
      case environment.bsc.chainIdInt:
        balance = evmBalanceData.nativeBalBSC as number;
        break;
      case environment.sol.chainIdInt:
        balance = solBalanceData.nativeBalSol as number;
        break;
      default:
        balance = evmBalanceData.nativeBalETH as number;
        break;
    }
    return balance;
  }

  checkChain(): boolean {
    if (this.activeChain === CHAINS.SOL) {
      return true;
    }
    const isWert = this.isWert();
    if (isWert && this.dataService.chainId == environment.eth.chainIdInt) {
      return true;
    }
    if (isWert) {
      this.dataService.switchNetwork(environment.eth.chainIdInt);
      return false;
    }
    if (this.dataService.chainId == this.activeToken.chainId) {
      return true;
    } else {
      this.dataService.switchNetwork(this.activeToken.chainId).then(() => {
        return true;
      });
      return false;
    }
  }

  convertNum(amount: number, decimals: number = 2) {
    return convertNum(amount, decimals);
  }

  floorString(str: string) {
    let pos = str.indexOf('.');
    return pos >= 0 ? (str + '00').slice(0, pos + 6) : str + '.00';
  }

  changeTab(tab: string, symbol: string): void {
    const activeCrypto = this.tokenList.find((item: ITokenData) => item.category === tab && item.symbol === symbol);
    if (activeCrypto) {
      this.setActiveTab(activeCrypto);
    } else {
      // No matching token for the selected tab/symbol — no-op
    }
    this.messageEvent.emit(false);
    setTimeout(() => this.max(), 1000);
  }

  cardMode(): void {
    this.swapToken = 0;
    this.baseToken = 0;
    this.isNative.set(false);
    this.messageEvent.emit(true);
  }

  stakeNow(): void {
    this.dataService.switchNetwork(environment.eth.chainIdInt);
    this.router.navigateByUrl((this.currentLang !== 'en' ? this.currentLang : '') + '/staking', {});
  }

  setTab(tab: string, event?: Event, tabClick: boolean = true) {
    if (!!event && this.selectedTab === tab) return; // prevent duplicate click
    this.selectedTab = tab;
    if (event) event.stopPropagation();
    this.tokenFilteredList = this.tokenList.filter((item: ITokenData) => item.category === tab || tab === 'ALL');
    // this.tokenFilteredList = this.tokenFilteredList.filter((token) => (this.selectedChain === CHAINS.SOLANA ? token.category === 'SOL' : token.category !== 'SOL'));
    this.tokenFilteredList.sort(({ tokenUsdVal: a }, { tokenUsdVal: b }) => b - a);
    if (this.walletAddress) {
      if (this.activeChain === CHAINS.EVM && this.selectedTab === 'SOL') {
        if (!this.dataService.walletAddressSOL) {
          this.dataService.connectWallet('SOL');
        }
      }
      if (this.activeChain === CHAINS.SOL && (this.selectedTab === 'ETH' || this.selectedTab === 'BNB')) {
        if (!this.dataService.walletAddress) {
          this.dataService.connectWallet('ETH');
        }
      }
    }
    if (!tabClick) this.setActiveTab(this.tokenFilteredList[0], !tabClick);
  }

  async setActiveTab(item: ITokenData, autoTab: boolean = false) {
    this.activeToken = item;
    this.activeToken.symbol = this.activeToken.symbol.toString();
    this.tokenName = this.isWert() ? 'USD' : (this.activeToken?.symbol ?? 'USDC');
    this.isNative.set(item.symbol !== 'USDT' && item.symbol !== 'USDC');
    setTimeout(() => this.max(), 1000);
    const chain = item.category === 'SOL' ? CHAINS.SOL : CHAINS.EVM;
    this.activeChain = chain;
    this.dataService.activeChain = chain;
    this.dataService.updateCurrentNetwork();
    if (!autoTab) {
      this.dropDownBtn()?.nativeElement.click();
    }
  }

  openEthBuyWidget(isBscChain = false) {
    this.modalRef = this.modalService.show(BuyETHWertComponent, {
      class: 'modal-xs modal-dialog-centered transak',
      backdrop: 'static',
      initialState: { walletAddress: this.walletAddress, isBscChain },
    });
  }

  // Multi connection Items
  async changeChain(fromCardCTA = false) {
    if (fromCardCTA) {
      this.isWert.set(true);
      return;
    }
  }

  toggleWert(): void {
    // Flip wert mode — model() propagates the new value back to the parent via two-way binding
    this.isWert.set(!this.isWert());
    this.max();
  }
}
